import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { TUI_NOTIFICATION_OPTIONS } from '@taiga-ui/core/tokens';
import { TuiAlertOptions } from '@taiga-ui/core/interfaces';
import { TuiNotification } from '@taiga-ui/core';
import { AbstractTuiDialogService, TuiIdService } from '@taiga-ui/cdk';
import { TranslateService } from '@ngx-translate/core';
import { inject, Inject, Injectable } from '@angular/core';

import { ToastConfig, ToastMsg, ToastRef } from './toast.types';
import { ToastComponent } from './toast.component';

@Injectable({ providedIn: 'root' })
export class ToastService extends AbstractTuiDialogService<TuiAlertOptions<any>> {
  private readonly translate = inject(TranslateService);
  protected readonly component = new PolymorpheusComponent(ToastComponent);

  constructor(
    @Inject(TUI_NOTIFICATION_OPTIONS)
    protected readonly defaultOptions: TuiAlertOptions<any>,
    @Inject(TuiIdService) idService: TuiIdService,
  ) {
    super(idService);
  }

  public warning(msg: ToastMsg, config?: Omit<ToastConfig, 'type'>): ToastRef {
    return this.msg(msg, { type: TuiNotification.Warning, ...config });
  }

  public success(msg: ToastMsg, config?: Omit<ToastConfig, 'type'>): ToastRef {
    return this.msg(msg, { type: TuiNotification.Success, ...config });
  }

  public error(msg: ToastMsg, config?: Omit<ToastConfig, 'type'>): ToastRef {
    return this.msg(msg, { type: TuiNotification.Error, ...config });
  }

  public msg(msg: ToastMsg, config: ToastConfig): ToastRef {
    const id = +new Date();
    const hasCloseButton = config.dismissible !== false;
    const autoClose = !hasCloseButton || config.timeout === 0 ? false : (config.timeout ?? 5000);
    const sub = this.open(this.translateMsg(msg, config), {
      status: config.type,
      hasCloseButton,
      hasIcon: false,
      autoClose,
    }).subscribe();

    return { id, msg, config, dismiss: () => sub.unsubscribe() };
  }

  private translateMsg(msg: ToastMsg, config?: ToastConfig): ToastMsg {
    if (typeof msg === 'string') {
      return config?.withoutTranslation ? msg : this.translate.instant(msg, config?.translationKeys) as string || '';
    }
    return msg;
  }
}
